import { useEffect, useRef, useState } from "react";
import SummaryApi from "../common/SummaryApi";
import apiRequest from "../common/apiRequest";
import { MdOutlineMyLocation } from "react-icons/md";
import { X, Search } from "lucide-react";
import { toast } from "react-hot-toast";

const AddressSelectModal = ({ open, onClose, onAddNew }) => {
  const modalRef = useRef(null);

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const isLoggedIn = Boolean(localStorage.getItem("token"));

  /* ------------------ CLOSE ON OUTSIDE CLICK ------------------ */
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
        if (
        modalRef.current &&
        !modalRef.current.contains(e.target)
        ) {
        setTimeout(onClose, 0);
        }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  /* ------------------ LOAD ADDRESSES ------------------ */
  const loadAddresses = async () => {
    try {
        setLoading(true);
        const res = await apiRequest(SummaryApi.getMyAddresses);
        const list = res.addresses || [];

        setAddresses(list);

        // 🔥 AUTO SAVE DEFAULT ADDRESS IF NOT IN LOCAL STORAGE
        const saved = localStorage.getItem("selected_address");
        if (!saved) {
        const defaultAddr = list.find(a => a.isDefault);
        if (defaultAddr) {
            localStorage.setItem(
            "selected_address",
            JSON.stringify(defaultAddr)
            );
        }
        }

    } catch {
        toast.error("Failed to load addresses");
    } finally {
        setLoading(false);
    }
  };

    useEffect(() => {
    if (open && isLoggedIn) {
        loadAddresses();
    }
    }, [open, isLoggedIn]);


  /* ------------------ SET DEFAULT ADDRESS ------------------ */
   const handleSetDefault = async (addr) => {
    try {
        await apiRequest({
        ...SummaryApi.setDefaultAddress,
        url: SummaryApi.setDefaultAddress.url(addr._id),
        });

        const updatedAddress = { ...addr, isDefault: true };

        // ✅ FORCE SAVE
        localStorage.setItem(
        "selected_address",
        JSON.stringify(updatedAddress)
        );

        toast.success("Delivery address updated");

        setAddresses(prev =>
        prev.map(a => ({
            ...a,
            isDefault: a._id === addr._id,
        }))
        );

        onClose();
    } catch (err) {
        console.error("Set default failed", err);
        toast.error("Failed to update address");
    }
   };

  /* ------------------ ENABLE CURRENT LOCATION ------------------ */
    const handleEnableLocation = () => {
    if (!navigator.geolocation) {
        toast.error("Geolocation not supported");
        return;
    }

    navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
        try {
            const { latitude, longitude } = coords;

            // 🔥 OpenStreetMap Reverse Geocoding
            const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await res.json();

            const address = data.address || {};

            const detectedAddress = {
            area:
                address.suburb ||
                address.neighbourhood ||
                address.residential ||
                "",
            city:
                address.city ||
                address.town ||
                address.village ||
                "",
            state: address.state || "",
            pincode: address.postcode || "",
            lat: latitude,
            lng: longitude,
            isDetected: true,
            };

            // ✅ Store locally (NO LOGIN NEEDED)
            localStorage.setItem(
            "selected_address",
            JSON.stringify(detectedAddress)
            );

            toast.success("Location detected");

            onClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to detect location");
        }
        },
        () => toast.error("Location permission denied")
    );
    };

  /* ------------------ FILTER ------------------ */
  const filteredAddresses = addresses.filter((addr) => {
    const q = search.toLowerCase();
    return (
      addr.name?.toLowerCase().includes(q) ||
      addr.area?.toLowerCase().includes(q) ||
      addr.city?.toLowerCase().includes(q) ||
      addr.state?.toLowerCase().includes(q) ||
      addr.pincode?.includes(q)
    );
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-2">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-2xl rounded-xl shadow-xl"
      >
        {/* ---------------- HEADER ---------------- */}
        <div className="relative px-5 pt-4 pb-3 border-b flex items-center justify-center">
        {/* TITLE – perfectly centered */}
        <h3 className="text-base font-semibold text-center">
            Location
        </h3>

        {/* CLOSE BUTTON – fixed right */}
        <button
            onClick={onClose}
            className="
            absolute right-4
            p-2 rounded-full
            bg-[#7E869E40]
            hover:bg-[#7E869E60]
            "
        >
            <X size={18} className="text-[#222222]" />
        </button>
        </div>


        {/* ---------------- CONTENT ---------------- */}
        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">

          {/* SEARCH */}
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a address"
              className="
                w-full pl-9 py-2.5 rounded-lg
                border border-[#B5CAFF]
                bg-[#F1F5FF]
                text-sm outline-none
              "
            />
          </div>

          {/* LOCATION + ADD */}
          <div className="bg-[#F1F5FF] rounded-xl px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex gap-3 items-start">
                <MdOutlineMyLocation
                  size={22}
                  className="text-[#007BFF] mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-[#007BFF]">
                    Detect current location
                  </p>
                  <p className="text-xs font-medium text-[#8080808C]">
                    Enable your current location
                  </p>
                </div>
              </div>

              <button
                onClick={handleEnableLocation}
                className="
                  border border-[#007BFF]
                  text-[#007BFF]
                  px-4 py-1.5
                  rounded-lg
                  text-sm font-medium
                "
              >
                Enable
              </button>
            </div>

            <div className="border-t border-dashed" />

            {isLoggedIn && (
            <button
                onClick={onAddNew}
                className="w-full text-center text-[#007BFF] text-sm font-medium"
            >
                Add New Address
            </button>
            )}
          </div>

          {/* SAVED ADDRESSES */}
          <div className="border border-[#B5CAFF] rounded-xl overflow-hidden">
            {loading ? (
              <p className="text-center text-sm py-6 text-[#8080808C]">
                Loading addresses...
              </p>
            ) : filteredAddresses.length === 0 ? (
              <p className="text-center text-sm font-medium py-6 text-[#8080808C]">
                No saved addresses
              </p>
            ) : (
              filteredAddresses.map((addr, index) => (
               <div key={addr._id}>
                <label
                  className="
                    flex gap-3 p-4 cursor-pointer
                    hover:bg-gray-50
                  "
                >
                  {/* Checkbox */}
                <input
                type="checkbox"
                checked={addr.isDefault}
                onChange={() => handleSetDefault(addr)}
                className="
                    mt-1.5
                    h-5 w-5
                    rounded
                    accent-[#007BFF]
                    cursor-pointer
                    flex-shrink-0
                "
                />

                  {/* DETAILS */}
                  <div className="text-base leading-5 font-medium">
                    <p className="font-medium mb-2">
                      {addr.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {addr.flat}, {addr.area}, {addr.city},{" "}
                      {addr.state} {addr.pincode}
                    </p>
                    {addr.phone && (
                      <p className="text-sm text-gray-600 mt-1">
                        Phone number: {addr.phone}
                      </p>
                    )}
                  </div>
                </label>
                    {/* DASHED DIVIDER */}
                {index !== filteredAddresses.length - 1 && (
                <div className="border-t border-dashed border-gray-300 mx-4" />
                )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressSelectModal;
